import { ChevronRight, ErrorOutline, FlagOutlined } from '@mui/icons-material'
import { Box, CircularProgress, SxProps } from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAutoFlaggedCategoryNames } from '@omnea-app/fe-risks/settings/auto-flagged-categories/useAutoFlaggedCategoryNames'
import { DefaultChip } from '@omnea-app/frontend/components/shared/ui/chip/DefaultChip'
import { WithHint } from '@omnea-app/packages/universal/utility-types/utilityTypes'

const Icons = { error: ErrorOutline, flag: FlagOutlined }

type RiskColors = 'success' | 'warning' | 'error' | 'neutral'

interface FlaggedScoreChipProps {
  score: number
  tooltip?: WithHint<ReactNode, 'aggregate-request' | 'aggregate-submission' | 'category-count' | 'questions-count'>
  icon?: 'error' | 'flag'
  onClick?: () => void
  showArrow?: boolean
  color?: RiskColors
  sx?: SxProps
  autoFlaggedScoreCategoryIds?: string[]
}

const knownTooltips = ['aggregate-request', 'aggregate-submission', 'category-count', 'questions-count']

export function FlaggedScoreChip({
  score,
  tooltip,
  icon,
  onClick,
  showArrow = false,
  color = 'neutral',
  sx,
  autoFlaggedScoreCategoryIds,
}: FlaggedScoreChipProps) {
  const { t } = useTranslation('risks')
  const { names: categoryNames, isLoading } = useAutoFlaggedCategoryNames(autoFlaggedScoreCategoryIds)

  const dynamicTooltip = useMemo(() => {
    if (!tooltip) {
      return ''
    }

    if (typeof tooltip === 'string' && knownTooltips.includes(tooltip)) {
      return t(`${tooltip}-tooltip`)
    }

    return tooltip
  }, [tooltip, t])

  const labelWithCategories = isLoading ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress color="inherit" size={14} sx={{ mt: 1 }} />
    </Box>
  ) : (
    <>
      {categoryNames && tooltip === 'category-count' && categoryNames?.length > 0 && (
        <Box component="span" mr={0.5}>
          {categoryNames.join(', ')}:
        </Box>
      )}
      <Box component="span" fontWeight="600">
        {score}
      </Box>
    </>
  )

  const Icon = icon ? Icons[icon] : null

  return (
    <DefaultChip
      tooltipLabel={dynamicTooltip}
      label={labelWithCategories}
      color={color}
      icon={Icon && !isLoading ? <Icon sx={{ width: '14px !important' }} /> : undefined}
      endIcon={<ChevronRight sx={{ marginRight: '-5px !important', ml: '0px !important' }} />}
      onDelete={showArrow ? onClick || (() => ({})) : undefined}
      tooltipPlacement="right"
      sx={{
        width: 'fit-content',
        cursor: 'pointer',
        // If we're being rendered inside a disabled input
        '-webkit-text-fill-color': 'initial',
        ...sx,
      }}
      onClick={onClick}
    />
  )
}
